import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateScheduledQueryBody,
  UpdateScheduledQueryBody,
} from '@toggle/toggle';

import {
  createScheduledQuery,
  deleteScheduledQuery,
  resumeScheduledQuery,
  runScheduledQueryNow,
  suspendScheduledQuery,
  updateScheduledQuery,
} from '~/views/alfred/research/services/scheduled-query-service';

import {
  QUERY_KEY_SCHEDULED_QUERIES,
  QUERY_KEY_SCHEDULED_QUERY,
  QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS,
} from './useScheduledQueries';

export const useCreateScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateScheduledQueryBody) => createScheduledQuery(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERIES],
      });
    },
  });
};

export const useUpdateScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateScheduledQueryBody;
    }) => updateScheduledQuery(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERIES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERY, id],
      });
    },
  });
};

export const useDeleteScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteScheduledQuery(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERIES],
      });
      queryClient.removeQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERY, id],
      });
    },
  });
};

const invalidateScheduledQueryCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SCHEDULED_QUERIES] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SCHEDULED_QUERY, id] });
};

export const useSuspendScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: suspendScheduledQuery,
    onSuccess: (_data, id) => invalidateScheduledQueryCaches(queryClient, id),
  });
};

export const useResumeScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resumeScheduledQuery,
    onSuccess: (_data, id) => invalidateScheduledQueryCaches(queryClient, id),
  });
};

export const useRunScheduledQueryNow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => runScheduledQueryNow(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS, id],
      });
    },
  });
};
